import React from 'react';

import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { FloatingLabelComponent } from 'design/floatingLabel';
import { TextInputControlComponent } from 'design/textInputControl';
import { TextComponent } from 'design/text/textComponents';
import {
  PersonalInfoFormValues,
  PersonalInfoFormViolation,
} from 'types/personalInfoTypes';
import { OrgInfoFormValues, OrgInfoFormViolation } from 'types/orgInfoTypes';
import { createPersonalFormViolationLense } from 'utils/validatePersonalInfoFormValues';
import { createOrgInfoFormViolationLense } from 'utils/validateOrgInfoFormValues';
import { FieldsetLayoutComponent } from 'design/fieldsetLayout';
import { CountryInputComponent } from 'containers/CountryInputComponent';
import { PROD_STACK_POSITION_MAX_LENGTH } from 'utils/constants';

type Props = {
  personalInfoFormValues: PersonalInfoFormValues;
  personalInfoFormViolations: PersonalInfoFormViolation[];
  orgInfoFormValues: OrgInfoFormValues;
  orgInfoFormViolations: OrgInfoFormViolation[];
  disabled: boolean;
  onFirstNameChange(nextValue: string): void;
  onMiddleNameChange(nextValue: string): void;
  onLastNameChange(nextValue: string): void;
  onPositionChange(nextValue: string): void;
  onNameChange(nextValue: string): void;
  onLocationChange(nextValue: string): void;
};

const FIRST_NAME_MAX_LENGTH = 40;
const MIDDLE_NAME_MAX_LENGTH = 40;
const LAST_NAME_MAX_LENGTH = 40;
const ORG_NAME_MAX_LENGTH = 255;
const POSITION_INPUT_MAX_LENGTH = PROD_STACK_POSITION_MAX_LENGTH + 1;

export const ProdStackManagerClientInfoFieldset = (props: Props) => {
  const {
    disabled,
    personalInfoFormValues,
    personalInfoFormViolations,
    orgInfoFormValues,
    orgInfoFormViolations,
    onFirstNameChange,
    onMiddleNameChange,
    onLastNameChange,
    onPositionChange,
    onNameChange,
    onLocationChange,
  } = props;

  const {
    inputValidationRequired,
    prodStackManager: { clientInfoForm: ownDictionary },
  } = useIntlDictionary();

  const lastNameHint = React.useMemo(() => {
    for (let violation of personalInfoFormViolations) {
      switch (violation.name) {
        case 'LastNameMissed':
          return inputValidationRequired;
      }
    }
  }, [inputValidationRequired, personalInfoFormViolations]);

  const lastNameIsInvalid = personalInfoFormViolations.some((violation) =>
    createPersonalFormViolationLense(violation).isLastNameViolation()
  );

  const firstNameHint = React.useMemo(() => {
    for (let violation of personalInfoFormViolations) {
      switch (violation.name) {
        case 'FirstNameMissed':
          return inputValidationRequired;
      }
    }
  }, [inputValidationRequired, personalInfoFormViolations]);

  const firstNameIsInvalid = personalInfoFormViolations.some((violation) =>
    createPersonalFormViolationLense(violation).isFirstNameViolation()
  );

  const middleNameHint = React.useMemo(() => {
    for (let violation of personalInfoFormViolations) {
      switch (violation.name) {
        case 'MiddleNameMissed':
          return inputValidationRequired;
      }
    }
  }, [inputValidationRequired, personalInfoFormViolations]);

  const middleNameIsInvalid = personalInfoFormViolations.some((violation) =>
    createPersonalFormViolationLense(violation).isMiddleNameViolation()
  );

  const nameHint = React.useMemo(() => {
    for (let violation of orgInfoFormViolations) {
      switch (violation.name) {
        case 'NameMissed':
          return inputValidationRequired;
      }
    }
  }, [inputValidationRequired, orgInfoFormViolations]);

  const nameIsInvalid = orgInfoFormViolations.some((violation) =>
    createOrgInfoFormViolationLense(violation).isNameViolation()
  );

  const positionHint = React.useMemo(() => {
    for (let violation of personalInfoFormViolations) {
      switch (violation.name) {
        case 'PositionMissed':
          return inputValidationRequired;
        case 'PositionTooLong':
          return ownDictionary.positionMaxLengthHintText;
      }
    }
  }, [
    inputValidationRequired,
    ownDictionary.positionMaxLengthHintText,
    personalInfoFormViolations,
  ]);

  const positionIsInvalid = personalInfoFormViolations.some((violation) =>
    createPersonalFormViolationLense(violation).isPositionViolation()
  );

  const locationHint = React.useMemo(() => {
    for (let violation of orgInfoFormViolations) {
      switch (violation.name) {
        case 'LocationMissed':
          return inputValidationRequired;
      }
    }
  }, [inputValidationRequired, orgInfoFormViolations]);

  const locationIsInvalid = orgInfoFormViolations.some((violation) =>
    createOrgInfoFormViolationLense(violation).isLocationViolation()
  );

  return (
    <FieldsetLayoutComponent>
      <FloatingLabelComponent
        lifted={!!personalInfoFormValues.lastName}
        invalid={lastNameIsInvalid}
        titleText={
          <TextComponent color="secondary" size="m">
            {ownDictionary.lastNameInputLabelText}
          </TextComponent>
        }
        hintText={
          <TextComponent color="critical" size="s">
            {lastNameHint}
          </TextComponent>
        }
      >
        <TextInputControlComponent
          name="lastName"
          autoFocus
          value={personalInfoFormValues.lastName}
          disabled={disabled}
          invalid={lastNameIsInvalid}
          maxLength={LAST_NAME_MAX_LENGTH}
          onValueChange={onLastNameChange}
        />
      </FloatingLabelComponent>
      <FloatingLabelComponent
        lifted={!!personalInfoFormValues.firstName}
        invalid={firstNameIsInvalid}
        titleText={
          <TextComponent color="secondary" size="m">
            {ownDictionary.firstNameInputLabelText}
          </TextComponent>
        }
        hintText={
          <TextComponent color="critical" size="s">
            {firstNameHint}
          </TextComponent>
        }
      >
        <TextInputControlComponent
          name="firstName"
          value={personalInfoFormValues.firstName}
          disabled={disabled}
          invalid={firstNameIsInvalid}
          maxLength={FIRST_NAME_MAX_LENGTH}
          onValueChange={onFirstNameChange}
        />
      </FloatingLabelComponent>
      <FloatingLabelComponent
        lifted={!!personalInfoFormValues.middleName}
        invalid={middleNameIsInvalid}
        titleText={
          <TextComponent color="secondary" size="m">
            {ownDictionary.middleNameInputLabelText}
          </TextComponent>
        }
        hintText={
          <TextComponent color="critical" size="s">
            {middleNameHint}
          </TextComponent>
        }
      >
        <TextInputControlComponent
          name="middleName"
          value={personalInfoFormValues.middleName}
          disabled={disabled}
          invalid={middleNameIsInvalid}
          maxLength={MIDDLE_NAME_MAX_LENGTH}
          onValueChange={onMiddleNameChange}
        />
      </FloatingLabelComponent>
      <FloatingLabelComponent
        lifted={!!orgInfoFormValues.name}
        invalid={nameIsInvalid}
        titleText={
          <TextComponent color="secondary" size="m">
            {ownDictionary.nameInputLabelText}
          </TextComponent>
        }
        hintText={
          <TextComponent color="critical" size="s">
            {nameHint}
          </TextComponent>
        }
      >
        <TextInputControlComponent
          name="orgName"
          value={orgInfoFormValues.name}
          disabled={disabled}
          invalid={nameIsInvalid}
          maxLength={ORG_NAME_MAX_LENGTH}
          onValueChange={onNameChange}
        />
      </FloatingLabelComponent>
      <FloatingLabelComponent
        lifted={!!personalInfoFormValues.position}
        invalid={positionIsInvalid}
        titleText={
          <TextComponent color="secondary" size="m">
            {ownDictionary.positionInputLabelText}
          </TextComponent>
        }
        hintText={
          <TextComponent color="critical" size="s">
            {positionHint}
          </TextComponent>
        }
      >
        <TextInputControlComponent
          name="position"
          value={personalInfoFormValues.position}
          disabled={disabled}
          invalid={positionIsInvalid}
          maxLength={POSITION_INPUT_MAX_LENGTH}
          onValueChange={onPositionChange}
        />
      </FloatingLabelComponent>
      <FloatingLabelComponent
        lifted={!!orgInfoFormValues.location}
        invalid={locationIsInvalid}
        titleText={
          <TextComponent color="secondary" size="m">
            {ownDictionary.locationInputLabelText}
          </TextComponent>
        }
        hintText={
          <TextComponent color="critical" size="s">
            {locationHint}
          </TextComponent>
        }
      >
        <CountryInputComponent
          name="orgLocation"
          value={orgInfoFormValues.location}
          invalid={locationIsInvalid}
          onChange={onLocationChange}
        />
      </FloatingLabelComponent>
    </FieldsetLayoutComponent>
  );
};
